import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography, Autocomplete, MenuItem, Box, IconButton
} from '@mui/material';
import { Save, Close } from '@mui/icons-material';
import { useFormik } from 'formik';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { SMPAPI } from '../../../../../fetch/smp';
import { IUser } from '../../../../../models/smp';
import { DesktopDatePicker } from '@mui/lab';

const useStyles = makeStyles<Theme>(() => ({
  header: {
    backgroundColor: '#005570',
    color: '#eff4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.25rem 1rem',
    fontWeight: 600,
    fontSize: '1.02rem',
    letterSpacing: 0.2,
    borderRadius: 4,
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '.85rem',
    color: '#222'
  }
}));

type Mode = 'assign' | 'edit';

interface LicenseOption {
  value: string;
  label: string;
  annualCost: number;
  startDate?: string;
  endDate?: string;
  cancelByDate?: string;
}
interface Option { value: string; label: string }

interface Props {
  open: boolean;
  mode: Mode; // assign => create/attach new license. edit => patch existing license fields
  applicationID: number;
  selectedUserIds: number[];
  usersMap: Record<number, IUser>;
  onClose: () => void;
  onCompleted: () => void; // reload + clear selection
  showToast: (type: 'success' | 'error' | 'warning' | 'info', msg: string) => void;
}

const denseLabelStyle = {
  fontWeight: 500,
  fontSize: '0.75rem',
  color: '#222',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
};

export const BulkLicenseModal: React.FC<Props> = ({
  open,
  mode,
  applicationID,
  selectedUserIds,
  usersMap,
  onClose,
  onCompleted,
  showToast,
}) => {
  const classes = useStyles();
  const isAssign = mode === 'assign';
  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [subStatuses, setSubStatuses] = useState<Option[]>([]);
  const [tierOptions, setTierOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const initializedRef = useRef(false);

  // Fresh initial values for the form each time the modal opens
  const defaultValues = useMemo(() => ({
    assignedLicense: '',
    appTierType: '',
    applicationSubStatusID: '',
    applicationSubStatus: '',
    dateAdded: null as Date | null,
    accessRemoved: null as Date | null,
    licenseRemoved: null as Date | null,
    annualizedCost: '' as number | string,
  }), []);

  useEffect(() => {
    if (!open) return;
    initializedRef.current = true;
    setLoading(true);
    (async () => {
      try {
        const licRes = await SMPAPI.fetchLicenses(applicationID, { page: 1, perPage: 1000 });
        setLicenses(licRes.records.map(l => ({
          value: l.licenseID?.toString() || '',
            label: l.licenseName || `License ${l.licenseID}`,
          annualCost: Number(l.annualCost) || 0,
        })));
        setSubStatuses(await SMPAPI.fetchUserSubStatusOptions());
        setTierOptions(await SMPAPI.fetchAppTierTypeOptions());
      } catch {
        showToast('error', 'Failed to load license metadata');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, applicationID, showToast]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: defaultValues,
    validate: values => {
      const errors: Record<string, string> = {};
      if (isAssign && !values.assignedLicense) errors.assignedLicense = 'Required';

      const selected = licenses.find(l => l.value === values.assignedLicense);

      function ensureValidDate(field: 'dateAdded' | 'accessRemoved' | 'licenseRemoved') {
        const v = values[field];
        if (v == null) return;
        const d = v instanceof Date ? v : new Date(v as any);
        if (isNaN(d.getTime())) {
          errors[field] = 'Invalid date format';
        }
      }

      ensureValidDate('dateAdded');
      ensureValidDate('accessRemoved');
      ensureValidDate('licenseRemoved');

      // License window validation similar to AddEditUserModal
      if (values.dateAdded && selected?.startDate) {
        if (new Date(values.dateAdded) < new Date(selected.startDate)) {
          errors.dateAdded = 'Date Added cannot be before license start date';
        }
      }
      if (values.dateAdded && values.accessRemoved && values.dateAdded > values.accessRemoved) {
        errors.dateAdded = 'Date Added cannot be after Access Removed';
        errors.accessRemoved = 'Access Removed cannot be before Date Added';
      }
      if (values.dateAdded && values.licenseRemoved && values.dateAdded > values.licenseRemoved) {
        errors.dateAdded = 'Date Added cannot be after License Removed';
        errors.licenseRemoved = 'License Removed cannot be before Date Added';
      }
      return errors;
    },
    onSubmit: async values => {
      setSubmitting(true);
      try {
        if (isAssign) {
          // Assign new license records: treat each user as creating/attaching a new license assignment
          let created = 0, skipped = 0; 
          for (const id of selectedUserIds) {
            const base = usersMap[id];
            if (!base) continue;
            // If user already has the exact license and we consider unique by assignedLicense, skip
            if (base.assignedLicense && String(base.assignedLicense) === values.assignedLicense) {
              skipped++; continue;
            }
            await SMPAPI.upsertUser({
              ...base,
              applicationUserID: undefined, // force new record creation
              employeeID: base.employeeID,
              applicationID,
              assignedLicense: values.assignedLicense,
              appTierType: values.appTierType || base.appTierType,
              applicationSubStatus: values.applicationSubStatus || base.applicationSubStatus,
              applicationSubStatusID: values.applicationSubStatusID || base.applicationSubStatusID,
              annualizedCost: values.annualizedCost || base.annualizedCost,
              dateAdded: values.dateAdded || base.dateAdded,
              accessRemoved: values.accessRemoved || null,
              licenseRemoved: values.licenseRemoved || null,
              approvalNotes: base.approvalNotes,
              otherNotes: base.otherNotes,
            } as IUser);
            created++;
          }
          showToast('success', `Created ${created} record(s). Skipped ${skipped} duplicate(s).`);
        } else {
          // Edit existing assignments in place. Only overwrite provided fields.
          let updated = 0;
          for (const id of selectedUserIds) {
            const base = usersMap[id];
            if (!base) continue;
            const payload: IUser = { ...base } as IUser;
            if (values.assignedLicense) {
              payload.assignedLicense = values.assignedLicense;
              if (values.annualizedCost) payload.annualizedCost = values.annualizedCost as number;
            }
            if (values.appTierType) payload.appTierType = values.appTierType;
            if (values.applicationSubStatusID) {
              payload.applicationSubStatusID = values.applicationSubStatusID as any;
              payload.applicationSubStatus = values.applicationSubStatus;
            }
            if (values.dateAdded) payload.dateAdded = values.dateAdded as any;
            if (values.accessRemoved) payload.accessRemoved = values.accessRemoved as any;
            if (values.licenseRemoved) payload.licenseRemoved = values.licenseRemoved as any;
            await SMPAPI.upsertUser(payload);
            updated++;
          }
          showToast('success', `Updated ${updated} assignment(s).`);
        }
        onCompleted();
        onClose();
      } catch (e: any) {
        showToast('error', e?.detail || 'Bulk operation failed');
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Reset the form whenever the modal opens or mode changes so entries don't persist between sessions
  useEffect(() => {
    if (open) {
      formik.resetForm({ values: defaultValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAssign, defaultValues]);

  const handleClose = () => {
    // Ensure form is cleared on close so a fresh session starts next time it opens
    formik.resetForm({ values: defaultValues });
    onClose();
  };

  const selectedLicense = useMemo(() => licenses.find(l => l.value === formik.values.assignedLicense), [licenses, formik.values.assignedLicense]);

  useEffect(() => {
    if (selectedLicense) formik.setFieldValue('annualizedCost', selectedLicense.annualCost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLicense?.value]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2.5 }}>
        <Typography variant="h6">{isAssign ? 'Bulk Assign Licenses' : 'Bulk Edit Licenses'}</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">{selectedUserIds.length} user(s)</Typography>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ px: 2.5, py: 1 }}>
          <Box bgcolor="#f5f5f5" borderRadius={2} px={2} py={2}>
            <Box mb={2}>
              <Typography className={classes.header} variant="body1">
                {isAssign ? 'New License Assignment Details' : 'Edit License Assignment Details'}
              </Typography>
            </Box>
            <Grid container spacing={0.5}>
              <Grid item xs={5}><Typography sx={denseLabelStyle}>Assign License{isAssign && '*'}</Typography></Grid>
              <Grid item xs={5}>
                <Autocomplete
                  size="small"
                  loading={loading}
                  options={licenses}
                  value={licenses.find(l => l.value === formik.values.assignedLicense) || null}
                  getOptionLabel={o => o.label}
                  onChange={(_, v) => formik.setFieldValue('assignedLicense', v ? v.value : '')}
                  renderInput={params => <TextField {...params} placeholder={isAssign ? 'Select license' : ''} error={!!formik.errors.assignedLicense && formik.touched.assignedLicense} helperText={formik.touched.assignedLicense && formik.errors.assignedLicense} />}
                />
              </Grid>
              <Grid item xs={2} />

              <Grid item xs={5}><Typography sx={denseLabelStyle}>App Tier Type</Typography></Grid>
              <Grid item xs={5}>
                <TextField size="small" fullWidth select value={formik.values.appTierType} onChange={e => formik.setFieldValue('appTierType', e.target.value)}>
                  <MenuItem value=""><em>{isAssign ? 'Use user existing' : 'Leave unchanged'}</em></MenuItem>
                  {tierOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={2} />

              <Grid item xs={5}><Typography sx={denseLabelStyle}>User Application Sub Status</Typography></Grid>
              <Grid item xs={5}>
                <TextField size="small" fullWidth select value={formik.values.applicationSubStatusID} onChange={e => {
                  const sel = subStatuses.find(s => s.value === e.target.value);
                  formik.setFieldValue('applicationSubStatusID', sel ? sel.value : '');
                  formik.setFieldValue('applicationSubStatus', sel ? sel.label : '');
                }}>
                  <MenuItem value=""><em>{isAssign ? 'Use user existing' : 'Leave unchanged'}</em></MenuItem>
                  {subStatuses.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={2} />

              <Grid item xs={5}><Typography sx={denseLabelStyle}>Date Added</Typography></Grid>
              <Grid item xs={5}>
                <DesktopDatePicker
                  inputFormat="M/d/yyyy"
                  value={formik.values.dateAdded}
                  onChange={(date) => {
                    formik.setFieldValue('dateAdded', date);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('dateAdded', undefined);
                    }
                  }}
                  onAccept={(date) => {
                    formik.setFieldTouched('dateAdded', true);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('dateAdded', undefined);
                    }
                  }}
                  onClose={() => formik.setFieldTouched('dateAdded', true)}
                  minDate={selectedLicense?.startDate ? new Date(selectedLicense.startDate) : undefined}
                  maxDate={(() => {
                    const c = [
                      selectedLicense?.endDate && new Date(selectedLicense.endDate as any),
                      selectedLicense?.cancelByDate && new Date(selectedLicense.cancelByDate as any),
                      new Date()
                    ].filter(Boolean) as Date[];
                    return c.length ? new Date(Math.min(...c.map(d => d.getTime()))) : undefined;
                  })()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="dateAdded"
                      fullWidth
                      size="small"
                      onBlur={formik.handleBlur}
                      error={!!(formik.touched.dateAdded && formik.errors.dateAdded)}
                      helperText={formik.touched.dateAdded && formik.errors.dateAdded}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2} />

              <Grid item xs={5}><Typography sx={denseLabelStyle}>Access Removed</Typography></Grid>
              <Grid item xs={5}>
                <DesktopDatePicker
                  inputFormat="M/d/yyyy"
                  value={formik.values.accessRemoved}
                  onChange={(date) => {
                    formik.setFieldValue('accessRemoved', date);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('accessRemoved', undefined);
                    }
                  }}
                  onAccept={(date) => {
                    formik.setFieldTouched('accessRemoved', true);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('accessRemoved', undefined);
                    }
                  }}
                  onClose={() => formik.setFieldTouched('accessRemoved', true)}
                  minDate={formik.values.dateAdded || undefined}
                  maxDate={(() => {
                    const c = [
                      selectedLicense?.endDate && new Date(selectedLicense.endDate as any),
                      selectedLicense?.cancelByDate && new Date(selectedLicense.cancelByDate as any),
                      new Date()
                    ].filter(Boolean) as Date[];
                    return c.length ? new Date(Math.min(...c.map(d => d.getTime()))) : undefined;
                  })()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="accessRemoved"
                      fullWidth
                      size="small"
                      onBlur={formik.handleBlur}
                      error={!!(formik.touched.accessRemoved && formik.errors.accessRemoved)}
                      helperText={formik.touched.accessRemoved && formik.errors.accessRemoved}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2} />

              <Grid item xs={5}><Typography sx={denseLabelStyle}>License Removed</Typography></Grid>
              <Grid item xs={5}>
                <DesktopDatePicker
                  inputFormat="M/d/yyyy"
                  value={formik.values.licenseRemoved}
                  onChange={(date) => {
                    formik.setFieldValue('licenseRemoved', date);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('licenseRemoved', undefined);
                    }
                  }}
                  onAccept={(date) => {
                    formik.setFieldTouched('licenseRemoved', true);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('licenseRemoved', undefined);
                    }
                  }}
                  onClose={() => formik.setFieldTouched('licenseRemoved', true)}
                  minDate={formik.values.dateAdded || undefined}
                  maxDate={(() => {
                    const c = [
                      selectedLicense?.endDate && new Date(selectedLicense.endDate as any),
                      selectedLicense?.cancelByDate && new Date(selectedLicense.cancelByDate as any),
                      new Date()
                    ].filter(Boolean) as Date[];
                    return c.length ? new Date(Math.min(...c.map(d => d.getTime()))) : undefined;
                  })()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="licenseRemoved"
                      fullWidth
                      size="small"
                      onBlur={formik.handleBlur}
                      error={!!(formik.touched.licenseRemoved && formik.errors.licenseRemoved)}
                      helperText={formik.touched.licenseRemoved && formik.errors.licenseRemoved}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2} />

              {/* Helper message removed per request */}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleClose} startIcon={<Close />} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            startIcon={<Save />}
            disabled={submitting || (!!formik.errors.assignedLicense && isAssign)}
          >
            Apply
          </Button>
        </DialogActions>
      </form>
      {submitting && (
        <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.5)' }}>
          <Typography variant="body2">Processing...</Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default BulkLicenseModal;
